import { useEffect, useRef, useCallback, useState } from 'react'
import { useAppSelector } from './useAppDispatch'
import { SERVICE_URLS, SSE_RECONNECT_INITIAL_MS, SSE_RECONNECT_MAX_MS, SSE_RECONNECT_FIRST_CAP_MS, SSE_TOKEN_RECONNECT_DEBOUNCE_MS } from '../lib/config'
import { ENDPOINTS } from '../lib/endpoints'
import { SSE_EVENT_TYPES } from '@ekkam/contracts'

const SSE_BASE = SERVICE_URLS.sse

type SSEHandler = (data: unknown) => void

export type SSEStatus = 'connected' | 'disconnected' | 'reconnecting'

interface UseSSEOptions {
  discussionId?: string
  onEvent: Record<string, SSEHandler>
  enabled?: boolean
}

export const useSSE = ({ discussionId, onEvent, enabled = true }: UseSSEOptions): { status: SSEStatus } => {
  const { isAuthenticated, accessToken } = useAppSelector((s) => ({
    isAuthenticated: s.auth.isAuthenticated,
    accessToken: s.auth.accessToken,
  }))

  const esRef = useRef<EventSource | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(SSE_RECONNECT_INITIAL_MS)
  const mountedRef = useRef(true)
  const onEventRef = useRef(onEvent)
  const accessTokenRef = useRef(accessToken)
  const [status, setStatus] = useState<SSEStatus>('disconnected')

  // Keep refs updated without triggering reconnect
  useEffect(() => { onEventRef.current = onEvent })
  useEffect(() => { accessTokenRef.current = accessToken })

  const connect = useCallback(() => {
    if (!isAuthenticated || !accessTokenRef.current || !enabled || !mountedRef.current) return

    // Close existing connection
    if (esRef.current) {
      esRef.current.close()
      esRef.current = null
    }

    // Only show 'reconnecting' if we were previously connected and lost it.
    // If we still have a live connection (token rotation), stay 'connected'
    // silently — no need to flash a banner for a routine token refresh.
    setStatus((prev) => prev === 'connected' ? 'connected' : 'reconnecting')

    const url = new URL(`${SSE_BASE}${ENDPOINTS.sse.connect}`)
    if (discussionId) url.searchParams.set('discussionId', discussionId)
    url.searchParams.set('token', accessTokenRef.current)

    const es = new EventSource(url.toString(), { withCredentials: true })
    esRef.current = es

    es.addEventListener('connected', () => {
      reconnectDelayRef.current = SSE_RECONNECT_INITIAL_MS // reset backoff
      setStatus('connected')
    })

    // Use ref for handlers so they always have latest callbacks
    es.onmessage = (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data as string) as unknown
        onEventRef.current['message']?.(parsed)
      } catch {}
    }

    // Register typed event listeners from the shared contract
    for (const eventType of SSE_EVENT_TYPES) {
      es.addEventListener(eventType, (e: MessageEvent) => {
        if (eventType === 'ping') return
        try {
          const data = JSON.parse(e.data as string) as unknown
          onEventRef.current[eventType]?.(data)
        } catch {
          onEventRef.current[eventType]?.(e.data)
        }
      })
    }

    es.onerror = () => {
      es.close()
      esRef.current = null
      if (!mountedRef.current) return

      const delay = reconnectDelayRef.current
      reconnectDelayRef.current = Math.min(delay * 2, SSE_RECONNECT_MAX_MS)

      // Use a short grace period before showing 'disconnected' UI.
      // This prevents a flash banner on transient errors (tab re-focus,
      // a single dropped packet) that self-resolve within seconds.
      const gracePeriod = Math.min(delay, SSE_RECONNECT_FIRST_CAP_MS)
      reconnectTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return
        setStatus('reconnecting')
        connect()
      }, gracePeriod)
    }
  }, [isAuthenticated, enabled, discussionId]) // accessToken intentionally not in deps — use ref

  // Reconnect when token changes (after refresh)
  useEffect(() => {
    if (accessToken && isAuthenticated && enabled) {
      // Small delay to avoid reconnecting on every render
      const t = setTimeout(() => connect(), SSE_TOKEN_RECONNECT_DEBOUNCE_MS)
      return () => clearTimeout(t)
    }
  }, [accessToken, isAuthenticated, enabled, connect])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }
    }
  }, [])

  return { status }
}
